import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as multiScenarioActionCreators from '@makemydeal/dr-dash-store/actions/multiScenarioActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { constants } from '@makemydeal/dr-offer-redux';
import { CASH } from '@makemydeal/dr-platform-types';

import { DRAFT_DEAL_LIMIT } from '../../../constants';

export const useMultiScenarioActions = (scopeId: string) => {
    const dispatch = useDispatch();
    const sessionId = useSelector(offerReduxSelectors.getSessionId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const vin = useSelector(vehicleSelectors.getVehicleVin);
    const primaryScopeId = useSelector(scopedStateSelectors.getPrimaryScopeId);
    const isPrimaryScope = primaryScopeId === scopeId;
    const isPaymentCalculating = useSelector(paymentSelectors.isCalculatingPayment);
    const scopesOrder = useSelector(scopedStateSelectors.getInitializedScopesOrder);
    const enableScenarioDuplicationWithAutoSelect = useSelector(featureToggleSelectors.isScenarioDuplicationWithAutoSelectEnabled);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const currentCalcEndpoint = useSelector((state: any) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType)
    );
    const isAutoSelectChecked = useSelector(baseDealSelectors.isAutoSelectProgramChecked);
    const isLocked = dealLockHooks.useIsDealLocked();

    const isProgramQuotesEnabled = currentCalcEndpoint !== constants.CALC_ENDPOINTS.programQuotes;
    const isCashDeal = offerType === CASH;

    let isDuplicateDealAllowed: boolean;
    const underDraftDealLimit = scopesOrder.length < DRAFT_DEAL_LIMIT;

    if (enableScenarioDuplicationWithAutoSelect) {
        // Scenario duplication toggle ON - allow duplication with minimal restrictions
        isDuplicateDealAllowed = underDraftDealLimit && !isPaymentCalculating;
    } else {
        // Scenario duplication toggle OFF - use restrictive validation
        const meetsAutoSelectCriteria = !isProgramQuotesEnabled || isAutoSelectChecked || isCashDeal;
        isDuplicateDealAllowed = underDraftDealLimit && !isPaymentCalculating && meetsAutoSelectCriteria;
    }

    const logNRAction = useCallback(
        (name: string) => {
            logNewRelicPageAction(name, {
                sessionId,
                dealXgId,
                dealXgVersion,
                dealerId,
                vin
            });
        },
        [dealXgId, dealXgVersion, dealerId, sessionId, vin]
    );

    const deleteScope = useCallback(() => {
        dispatch(scopedActionsCreators.deleteScope(scopeId));
        dispatch(multiScenarioActionCreators.deleteDealScenarioClicked());
        logNRAction('MV:Multiscenario - Delete deal');
    }, [dispatch, scopeId, logNRAction]);

    const duplicateScope = useCallback(() => {
        dispatch(scopedActionsCreators.cloneScope(scopeId));
        dispatch(multiScenarioActionCreators.copyDealScenarioClicked());
        logNRAction('MV:Multiscenario - Duplicate deal');
    }, [dispatch, scopeId, logNRAction]);

    const makePrimary = useCallback(() => {
        dispatch(scopedActionsCreators.changeDefaultScope(scopeId));
        dispatch(multiScenarioActionCreators.selectPrimaryDealScenarioClicked());
        logNRAction('MV:Multiscenario - Change primary deal');
    }, [dispatch, scopeId, logNRAction]);

    return {
        isPrimaryScope,
        isDeleteDisabled: isPrimaryScope || isLocked,
        isDuplicateDisabled: !isDuplicateDealAllowed || isLocked,
        deleteScope,
        duplicateScope,
        makePrimary
    };
};
